import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  type AmpBoardAgentTurnsQuery,
  useAmpBoardAgentTurnsQuery,
  useAmpBoardAgentWorkQuery,
} from "src/graphql";
import { useGovernedRefetchInterval } from "src/hooks/use-governed-refetch-interval";
import {
  type AgentWork,
  agentTurnsSince,
  agentTurnsWhere,
  NO_AGENT_WORK,
  toAgentWork,
  type WorkingTurn,
  workingTurns,
} from "src/pages/projects/agent-work";

/** How often a running turn is checked, so its end shows within a few seconds. */
const WORKING_POLL_MS = 5_000;
// With nothing running, a move made in this browser refetches at once, and the board's change poll catches everyone else's moves.
const IDLE_POLL_MS = 15_000;
const IDLE_MAX_POLL_MS = 60_000;

const NO_TURNS: WorkingTurn[] = [];

const selectWorkingTurns = (data: AmpBoardAgentTurnsQuery) =>
  workingTurns(data.llm_chat_interactions, Date.now());

const turnKeyOf = (turn: WorkingTurn) =>
  [turn.sessionId, turn.interactionId, turn.startedAt].join(" ");

function turnOfKey(key: string): WorkingTurn {
  const [sessionId = "", interactionId = "", startedAt = ""] = key.split(" ");
  return { sessionId, interactionId, startedAt };
}

const AgentWorkContext = createContext<AgentWork>(NO_AGENT_WORK);

/**
 * Watches the projects agent's turns for the whole area, so every card, row
 * and page reads one poll instead of starting its own.
 */
export const AgentWorkProvider = ({ children }: { children: ReactNode }) => {
  const since = agentTurnsSince(Date.now());
  const idle = useGovernedRefetchInterval<WorkingTurn[]>({
    baseMs: IDLE_POLL_MS,
    maxMs: IDLE_MAX_POLL_MS,
  });
  const working = useGovernedRefetchInterval<WorkingTurn[]>({
    baseMs: WORKING_POLL_MS,
    maxMs: IDLE_POLL_MS,
  });
  // Only a running turn on a task or project shows, so a subagent's own session polls at the idle rate.
  const [hasWork, setHasWork] = useState(false);
  const lastModeRef = useRef<"working" | "idle">("idle");
  const { data: polledTurns = NO_TURNS } = useAmpBoardAgentTurnsQuery(
    { where: agentTurnsWhere(since) },
    {
      select: selectWorkingTurns,
      // The window's key changes every minute, and the turns it held still show meanwhile.
      keepPreviousData: true,
      // Both arguments forwarded, so the governed hooks can still tell a settled fetch from a render.
      refetchInterval: (data, query) => {
        const mode = data && data.length > 0 && hasWork ? "working" : "idle";
        const governor = mode === "working" ? working : idle;
        // A new turn polls fast again, and an ended one backs off from the start.
        if (mode !== lastModeRef.current) {
          lastModeRef.current = mode;
          governor.resetBackoff();
        }
        return governor.refetchInterval(data, query);
      },
    },
  );
  // `select` builds a new array on every window move, so key on the turns to keep cards and rows from re-rendering.
  const turnsKey = polledTurns.map(turnKeyOf).join(",");
  const turns = useMemo(
    () => (turnsKey ? turnsKey.split(",").map(turnOfKey) : NO_TURNS),
    [turnsKey],
  );
  const sessionIds = useMemo(
    () => turns.map((turn) => turn.sessionId),
    [turns],
  );
  const { data: work } = useAmpBoardAgentWorkQuery(
    { sessionIds },
    { enabled: sessionIds.length > 0, keepPreviousData: true },
  );

  // Consumed by every card and row, so it only changes when the work does.
  const value = useMemo(() => toAgentWork(turns, work), [turns, work]);
  // State, not a ref: the re-render it causes recomputes the turns query's interval, which reads it before `value` exists.
  useEffect(() => {
    setHasWork(value !== NO_AGENT_WORK);
  }, [value]);

  return (
    <AgentWorkContext.Provider value={value}>
      {children}
    </AgentWorkContext.Provider>
  );
};

/** Every task and project the agent is working on now. */
export const useAgentWork = (): AgentWork => useContext(AgentWorkContext);

export const useIsAgentWorkingOnTask = (taskId: string): boolean =>
  useContext(AgentWorkContext).taskIds.has(taskId);

/** The agent's turn running on a task now, or null when it isn't working on it. */
export const useAgentTurnOnTask = (taskId: string): WorkingTurn | null =>
  useContext(AgentWorkContext).turnByTaskId.get(taskId) ?? null;
